import type { ReactNode } from 'react';
import {
  ExternalLink,
  MoreHorizontal,
  Pause,
  Play,
} from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@loader/ui';
import { Link } from '@tanstack/react-router';

import type { SyncStatus } from './shared';
import { SyncStatusDot, SyncStatusBadge, ScheduleInfo, SyncNowMenuItem } from './shared';

import type { SyncProgressInfo } from '../hooks/useSyncTrigger';
import { formatRelativeTime } from '@/lib/formatters';

export interface ConnectorSyncRowProps {
  /** Primary display name (sync name for Nango, table name for custom) */
  name: string;
  /** Secondary label shown after the name (e.g. table name for Nango) */
  subtitle?: string;
  tableId: string;
  status: SyncStatus;
  schedule: string | null | undefined;
  /** Last synced timestamp (ISO string). Shown when provided. */
  lastSyncedAt?: string | null;
  /** Live fetch progress (records fetched + optional total). Shown in badge when syncing. */
  progress?: SyncProgressInfo | null;
  onTriggerSync: () => void;
  onPause?: () => void;
  onResume?: () => void;
  /** Extra menu items rendered before "Open Table" (e.g. "Copy Endpoint URL", "Schedule") */
  extraMenuItems?: ReactNode;
  /** Hide sync/pause actions (for push-based connectors like post_api) */
  hideScheduleActions?: boolean;
}

export function ConnectorSyncRow({
  name,
  subtitle,
  tableId,
  status,
  schedule,
  lastSyncedAt,
  progress,
  onTriggerSync,
  onPause,
  onResume,
  extraMenuItems,
  hideScheduleActions,
}: ConnectorSyncRowProps) {
  const isPaused = status === 'paused';

  return (
    <div className="border-border/40 flex items-center gap-3 border-b px-4 py-2 pl-10 last:border-b-0">
      <SyncStatusDot status={status} />

      {/* Name + open link + optional subtitle */}
      <div className="flex min-w-0 flex-1 items-baseline gap-1.5">
        <span className="truncate text-sm font-medium">{name}</span>
        <Link
          to="/workspace"
          search={{ preview: tableId, type: 'table' as const, expanded: true }}
          className="text-muted-foreground hover:text-foreground shrink-0"
        >
          <ExternalLink className="h-3 w-3" />
        </Link>
        {subtitle && (
          <span className="text-muted-foreground truncate text-sm">{subtitle}</span>
        )}
      </div>

      {/* Right side: badge + schedule + last sync + menu */}
      <div className="flex items-center gap-2">
        <SyncStatusBadge status={status} progress={progress} />
        {!hideScheduleActions && <ScheduleInfo schedule={schedule} />}

        {lastSyncedAt && (
          <span className="text-muted-foreground text-xs">
            {formatRelativeTime(lastSyncedAt)}
          </span>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {!hideScheduleActions && (
              <>
                <SyncNowMenuItem
                  isSyncing={status === 'syncing'}
                  disabled={isPaused}
                  onTrigger={onTriggerSync}
                />
                {isPaused ? (
                  <DropdownMenuItem onClick={onResume}>
                    <Play className="mr-2 h-3.5 w-3.5" />
                    Resume
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={onPause}>
                    <Pause className="mr-2 h-3.5 w-3.5" />
                    Pause
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
              </>
            )}
            {extraMenuItems}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
